/**
 * Link tags a Natalie link carries as `ctx_` query params (e.g. `?ctx_division=finance`),
 * sent on the token request so the conversation's transcript records them and a
 * customer can group transcripts by tag.
 *
 * Read from the current URL at token time: every step between the landing page and
 * the room, the sign-in redirect included, keeps the full query string on the URL.
 * natalie-api owns the name, value and count limits, so nothing is checked here
 * beyond the prefix. Values are opaque tags — never log them.
 */

import type { components } from './generated/natalie-api';

type CtxParams = NonNullable<components['schemas']['NatalieV2TokenRequestDto']['ctxParams']>;

/** Lowercase only: `CTX_` and `Ctx_` are not link tags. */
const CTX_PARAM_PREFIX = 'ctx_';

/**
 * The `ctx_` params in a search string, keyed by their exact names, or `undefined`
 * when there are none so the request body stays as it was without them. The first
 * occurrence of a repeated name wins. `URLSearchParams` decodes `+`/`%20` and never
 * throws on malformed percent-encoding.
 */
export const extractCtxParams = (search: string): CtxParams | undefined => {
  const ctxParams: CtxParams = {};
  for (const [name, value] of new URLSearchParams(search)) {
    if (name.startsWith(CTX_PARAM_PREFIX) && !(name in ctxParams)) ctxParams[name] = value;
  }
  return Object.keys(ctxParams).length > 0 ? ctxParams : undefined;
};
